import React, { useState, useEffect } from 'react';
import { 
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface VaccinationStatus {
  vaccine_type: string;
  vaccine_name: string;
  last_vaccination_date?: string;
  days_since_last?: number;
  next_due_date?: string;
  days_until_due?: number;
  status: string;
  is_critical: boolean;
}

interface DewormingStatus {
  last_deworming_date?: string;
  days_since_last?: number;
  next_due_date?: string;
  days_until_due?: number;
  status: string;
  is_critical: boolean;
}

interface GoatHealthComplianceRow {
  tag: string;
  goat_id: string;
  category: string;
  shed_id?: string;
  shed_name?: string;
  vaccination_statuses: VaccinationStatus[];
  deworming_status: DewormingStatus;
  treatment_count: number;
  last_treatment_date?: string;
  health_cost: number;
  requires_attention: boolean;
}

interface HealthComplianceSummary {
  total_goats: number;
  vaccination_coverage_percentage: number;
  deworming_coverage_percentage: number;
  goats_due_vaccination: number;
  goats_overdue_vaccination: number;
  goats_due_deworming: number;
  goats_overdue_deworming: number;
  goats_requiring_attention: number;
  avg_days_since_last_vaccination?: number;
  avg_days_since_last_deworming?: number;
  avg_health_cost_per_goat: number;
  total_health_cost: number;
}

interface MedicineUsageStat {
  medicine: string;
  usage_count: number;
  total_cost: number;
  goats_treated: number;
}

interface GoatHealthComplianceReport {
  location_id: string;
  location_name: string;
  report_date: string;
  summary: HealthComplianceSummary;
  goat_compliance: GoatHealthComplianceRow[];
  medicine_usage: MedicineUsageStat[];
}

const GoatHealthComplianceReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatHealthComplianceReport | null>(null);
  const [filterRequiresAttention, setFilterRequiresAttention] = useState(false);

  useEffect(() => {
    if (selectedLocation?.id) {
      fetchReport();
    }
  }, [selectedLocation?.id]);

  const fetchReport = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/health-compliance', {
        params: {
          location_id: selectedLocation.id,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load health compliance report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string, isCritical: boolean) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    if (isCritical || status === 'overdue' || status === 'never_vaccinated' || status === 'never_dewormed') {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800 flex items-center gap-1`}>
          <XCircleIcon className="h-3 w-3" />
          {status.replace('_', ' ').toUpperCase()}
        </span>
      );
    } else if (status === 'due') {
      return (
        <span className={`${baseClasses} bg-yellow-100 text-yellow-800 flex items-center gap-1`}>
          <ExclamationTriangleIcon className="h-3 w-3" />
          DUE
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800 flex items-center gap-1`}>
          <CheckCircleIcon className="h-3 w-3" />
          UP TO DATE
        </span>
      );
    }
  };

  const filteredGoats = report?.goat_compliance.filter(goat => 
    !filterRequiresAttention || goat.requires_attention
  ) || [];

  if (loading) {
    return (
      <div className="container-mobile py-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <ShieldCheckIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Health & Vaccination Compliance Report</h1>
        <p className="text-lg text-gray-600">
          Track vaccination status, deworming schedules, and health compliance
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Summary Cards */}
      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900">{report.summary.total_goats}</div>
              <div className="text-sm text-gray-600">Total Goats</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-green-600">
                {report.summary.vaccination_coverage_percentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Vaccination Coverage</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-blue-600">
                {report.summary.deworming_coverage_percentage.toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Deworming Coverage</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-red-100 p-4 border-red-300">
              <div className="text-2xl font-bold text-red-600">
                {report.summary.goats_requiring_attention}
              </div>
              <div className="text-sm text-gray-600">Require Attention</div>
            </div>
          </div>

          {/* Alert Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Compliance Alerts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                <div className="text-lg font-semibold text-yellow-800">
                  {report.summary.goats_due_vaccination}
                </div>
                <div className="text-sm text-yellow-700">Due for Vaccination</div>
              </div>
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <div className="text-lg font-semibold text-red-800">
                  {report.summary.goats_overdue_vaccination}
                </div>
                <div className="text-sm text-red-700">Overdue Vaccination</div>
              </div>
              <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                <div className="text-lg font-semibold text-yellow-800">
                  {report.summary.goats_due_deworming}
                </div>
                <div className="text-sm text-yellow-700">Due for Deworming</div>
              </div>
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <div className="text-lg font-semibold text-red-800">
                  {report.summary.goats_overdue_deworming}
                </div>
                <div className="text-sm text-red-700">Overdue Deworming</div>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filterRequiresAttention}
                onChange={(e) => setFilterRequiresAttention(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-700">
                Show only goats requiring attention
              </span>
            </label>
          </div>

          {/* Goat Compliance Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Goat Compliance Details ({filteredGoats.length} goats)
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tag</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shed</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vaccinations</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deworming</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Treatments</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Health Cost</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGoats.map((goat, index) => (
                    <tr key={index} className={goat.requires_attention ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {goat.tag}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 capitalize">
                        {goat.category}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {goat.shed_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="space-y-1">
                          {goat.vaccination_statuses.map((vs, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">{vs.vaccine_name}:</span>
                              {getStatusBadge(vs.status, vs.is_critical)}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(goat.deworming_status.status, goat.deworming_status.is_critical)}
                        {goat.deworming_status.last_deworming_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            Last: {formatDate(goat.deworming_status.last_deworming_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {goat.treatment_count}
                        {goat.last_treatment_date && (
                          <div className="text-xs text-gray-500">
                            Last: {formatDate(goat.last_treatment_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(goat.health_cost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Medicine Usage */}
          {report.medicine_usage.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Medicine Usage Statistics</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medicine</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage Count</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Goats Treated</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.medicine_usage.map((medicine, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {medicine.medicine}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {medicine.usage_count}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {medicine.goats_treated}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {formatCurrency(medicine.total_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatHealthComplianceReportPage;

